/**
 * Persisted Storage Accessors
 *
 * The appId-scoped localStorage key and the raw read of the persisted blob.
 *
 * These live apart from `persistConfig.ts` because `bootstrap.ts` needs them on
 * every page load of every customer site, and `persistConfig.ts` also holds the
 * rehydration half (`partialize`, `mergePersistedState`) whose validator closure
 * only the widget tier needs. While both halves shared a module Rollup could not
 * cut between them, so the always-loaded tier carried the validators too.
 *
 * This module deliberately imports nothing at runtime. Anything added here ships
 * on every page load, so a new import is a bundle decision, not a detail.
 *
 * `currentAppId` is module state, which makes single-instancing a correctness
 * property rather than a preference: two copies of this module would give the
 * tier that writes credentials and the tier that reads them different storage
 * keys, silently. Nothing asserts it. It holds because both tiers are inputs to
 * one Rollup build; a second, separate build of the widget tier would break it.
 */

import type { WidgetStore } from '@/stores/storeTypes';

// Approximate one year in milliseconds.
export const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
export const STORE_NAME = 'widget-store';

/**
 * Storage structure for Zustand persist.
 * We store Zustand's value (which is { state: ..., version?: ... }) plus our metadata.
 */
export interface StorageData {
  updatedAt: number;
  value: { state: Partial<WidgetStore>; version?: number };
}

/**
 * Current appId for storage scoping.
 * Set via setAppIdForStorage() from bootstrap/startFullWidget before any storage operations.
 */
let currentAppId: string | null = null;

export function setAppIdForStorage(appId: string | null): void {
  currentAppId = appId;
}

export function getStorageKey(): string {
  return currentAppId ? `gladlyChat_${currentAppId}` : 'gladlyChat_default';
}

/**
 * The full storage key, store name included — the key the persist middleware
 * writes under. Assumes setAppIdForStorage has been called first.
 *
 * No production caller: the bootstrap reads through getPersistedState below, and
 * the storage adapter builds the same key itself from the name Zustand hands it.
 * Kept because it is the one place the key's shape is stated once, which is what
 * persistedStorage.test.ts asserts against.
 */
export function getFullStorageKey(): string {
  return `${getStorageKey()}-${STORE_NAME}`;
}

/**
 * Read and parse persisted state from localStorage.
 * Returns null if data doesn't exist or is expired.
 *
 * The bootstrap's read path for credentials, so it must not throw — see the note
 * in persistedStorage.test.ts. The storage adapter does not come through here;
 * it reads localStorage inline because Zustand needs the outer value, version
 * included, where this returns only the state.
 */
export function getPersistedState(): Partial<WidgetStore> | null {
  try {
    const fullKey = getFullStorageKey();
    const stored = localStorage.getItem(fullKey);
    if (!stored) {
      return null;
    }

    const parsed: StorageData = JSON.parse(stored);

    // Check if data is expired (1 year)
    if (parsed.updatedAt && Date.now() - parsed.updatedAt > ONE_YEAR_MS) {
      localStorage.removeItem(fullKey);
      return null;
    }

    return parsed.value?.state ?? null;
  } catch {
    return null;
  }
}
